import type { ContractDetail, BrandingExportElements } from '../../utils/contractsApi';

/**
 * Contract summary export (CLM).
 *
 * Builds a clean, self-contained one-pager from the contract's facts and opens it
 * in a new window ready to print - so counsel can save a PDF or print a brief to
 * share, without a server round-trip. All data comes from the detail payload; no
 * document content or privileged text beyond what the user already sees.
 */

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active', expiring_soon: 'Expiring soon', expired: 'Expired', unknown: 'No dates',
};

export function buildContractSummaryHtml(data: ContractDetail, branding?: BrandingExportElements): string {
  const parties = (data.parties || []).filter((p) => p?.name);
  const obligations = (data.obligations || []);
  const flags = (data.risk_flags || []);
  const rows = (label: string, value: string) =>
    `<tr><td class="k">${esc(label)}</td><td class="v">${value}</td></tr>`;

  const partiesHtml = parties.length
    ? parties.map((p) => `<li><strong>${esc(p.name)}</strong>${p.role ? ` - ${esc(p.role)}` : ''}</li>`).join('')
    : '<li class="muted">No parties recorded.</li>';

  const obligationsHtml = obligations.length
    ? `<table class="obl"><thead><tr><th>Obligation</th><th>Due</th><th>Status</th></tr></thead><tbody>${
        obligations.map((o) => `<tr>
          <td>${esc(o.description)}</td>
          <td>${fmtDate(o.due_date)}</td>
          <td>${o.status === 'done' ? 'Done' : o.overdue ? 'Overdue' : 'Open'}</td>
        </tr>`).join('')
      }</tbody></table>`
    : '<p class="muted">No obligations tracked.</p>';

  const flagsHtml = flags.length
    ? `<ul>${flags.map((f) => `<li><strong>${esc(f.title || 'Flag')}</strong>${f.severity ? ` (${esc(f.severity)})` : ''}${f.detail ? `: ${esc(f.detail)}` : ''}</li>`).join('')}</ul>`
    : '';

  // Firm branding (best-effort): accent colour on the title, a logo/firm-name
  // band at the top, firm name in the footer. Falls back to plain styling.
  const accent = (branding?.accent_color && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(branding.accent_color))
    ? branding.accent_color : '#16233f';
  const brandBand = (branding?.logo_data_url || branding?.firm_name || branding?.header_text)
    ? `<div class="brandband">
        ${branding?.logo_data_url ? `<img class="brandlogo" src="${esc(branding.logo_data_url)}" alt="" />` : ''}
        <div class="brandmeta">
          ${branding?.firm_name ? `<div class="brandfirm">${esc(branding.firm_name)}</div>` : ''}
          ${branding?.header_text ? `<div class="brandhdr">${esc(branding.header_text)}</div>` : ''}
        </div>
      </div>`
    : '';
  const footCredit = branding?.firm_name
    ? `Prepared by ${esc(branding.firm_name)} · generated with PONS on ${fmtDate(new Date().toISOString())}.`
    : `Generated by PONS on ${fmtDate(new Date().toISOString())}. For internal use.`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(data.title)} - Summary</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: #1a2233; margin: 0; padding: 40px; line-height: 1.5; }
  .wrap { max-width: 760px; margin: 0 auto; }
  .brandband { display: flex; align-items: center; gap: 14px; padding-bottom: 14px; margin-bottom: 18px;
               border-bottom: 2px solid ${accent}; }
  .brandlogo { max-height: 46px; max-width: 220px; object-fit: contain; }
  .brandmeta { font-size: 12px; color: #44506a; }
  .brandfirm { font-weight: 600; font-size: 14px; color: ${accent}; }
  .brandhdr { color: #8792a8; }
  h1 { font-size: 24px; margin: 0 0 4px; color: ${accent}; }
  .badges { margin: 8px 0 20px; }
  .badge { display: inline-block; font-size: 11px; padding: 2px 8px; border-radius: 999px;
           border: 1px solid #d9dee8; color: #44506a; margin-right: 6px; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: .06em; color: #8792a8;
       margin: 24px 0 8px; border-bottom: 1px solid #e7ebf2; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; }
  td.k { color: #6b778f; width: 180px; padding: 3px 8px 3px 0; vertical-align: top; font-size: 13px; }
  td.v { padding: 3px 0; font-size: 13px; }
  ul { margin: 6px 0; padding-left: 18px; }
  li { font-size: 13px; margin: 2px 0; }
  p { font-size: 13px; }
  .muted { color: #9aa3b5; }
  table.obl { margin-top: 6px; }
  table.obl th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .04em;
                 color: #8792a8; border-bottom: 1px solid #e7ebf2; padding: 4px 8px 4px 0; }
  table.obl td { font-size: 13px; padding: 5px 8px 5px 0; border-bottom: 1px solid #f0f3f8; vertical-align: top; }
  .foot { margin-top: 32px; padding-top: 10px; border-top: 1px solid #e7ebf2; font-size: 11px; color: #9aa3b5; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
<div class="wrap">
  ${brandBand}
  <h1>${esc(data.title)}</h1>
  <div class="badges">
    <span class="badge">${esc(STATUS_LABEL[data.status] || data.status)}</span>
    ${data.risk_level ? `<span class="badge">${esc(data.risk_level)} risk</span>` : ''}
    ${data.contract_type ? `<span class="badge">${esc(data.contract_type)}</span>` : ''}
  </div>

  ${data.summary ? `<h2>Summary</h2><p>${esc(data.summary)}</p>` : ''}

  <h2>Key terms</h2>
  <table>
    ${rows('Effective date', fmtDate(data.effective_date))}
    ${rows('Expiry date', fmtDate(data.expiry_date))}
    ${rows('Governing law', esc(data.governing_law || '—'))}
    ${rows('Contract value', esc(data.contract_value || '—'))}
    ${rows('Auto-renew', data.auto_renew == null ? '—' : data.auto_renew ? 'Yes' : 'No')}
    ${rows('Renewal notice', data.renewal_notice_days != null ? `${data.renewal_notice_days} days` : '—')}
  </table>

  <h2>Parties</h2>
  <ul>${partiesHtml}</ul>

  <h2>Obligations</h2>
  ${obligationsHtml}

  ${data.risk_summary || flags.length ? `<h2>Risk</h2>${data.risk_summary ? `<p>${esc(data.risk_summary)}</p>` : ''}${flagsHtml}` : ''}

  <div class="foot">${footCredit}</div>
</div>
<script>window.onload = function () { setTimeout(function () { window.print(); }, 250); };</script>
</body>
</html>`;
}

export function printContractSummary(data: ContractDetail, branding?: BrandingExportElements): void {
  const html = buildContractSummaryHtml(data, branding);
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}
